import Image from 'next/image';
import { useRouter } from 'next/router';

import { Anchor, Box, Divider, Heading, List, Modal, Paragraph, Toast, useToast } from '@zillow/constellation-10';

import {
  useRentalManagerUserPropertyEditWebApiHandler_displayTotalMonthlyPriceWithFee,
  useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useGetListingDetails } from '../../../hooks/queries/properties';

import ClipboardImage from '../../../assets/clipboard.svg';

import {
  emitLearnMoreEvent,
  emitTotalPriceModalInteractionEvent,
  EVENT_IDS,
} from '../../../analytics/events/singleFamilyTotalMonthlyPrice';
import { useAuth } from '../../AuthProvider';
import ModalFooter from '../ModalFooter';

type Props = {
  onClose: () => void;
  onSubmit: () => void;
  isSubmitLoading?: boolean;
};

const SFTotalMonthlyPriceWithFeeAcknowledgementModal = ({ onClose, onSubmit, isSubmitLoading = false }: Props) => {
  const { enqueueToast } = useToast();

  const router = useRouter();
  const { user } = useAuth();
  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const { data: { lease, ...webAnalyticsData } = {} } = useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get(
    {
      listingAlias,
      includeLeases: true,
    },
    { enabled: Boolean(listingAlias) },
  );

  const modularListingProgressStep = listingDetails?.modularListingProgress?.step;

  const { mutateAsync: setDisplayTotalMonthlyPriceWithFee } =
    useRentalManagerUserPropertyEditWebApiHandler_displayTotalMonthlyPriceWithFee();

  const csEventsHandler = ({ triggerSource }: { triggerSource: string }) => {
    emitTotalPriceModalInteractionEvent({
      user,
      currentProperty: webAnalyticsData,
      lease,
      triggerSource,
      triggerLocation: !modularListingProgressStep?.includes('review')
        ? 'posting_path'
        : 'posting_path_review|off_market_listing',
    });
  };

  const onSubmitHandler = async () => {
    csEventsHandler({ triggerSource: 'button_to_confirm' });

    try {
      await setDisplayTotalMonthlyPriceWithFee({
        getParams: { listingAlias, displayTotalMonthlyPriceWithFee: true },
      });

      onSubmit();
    } catch (e) {
      enqueueToast(
        <Toast tone="critical">
          There was an error enabling display total monthly price with fee. Refresh the page and try again.
        </Toast>,
      );
    }
  };

  return (
    <Modal.Root
      open={true}
      onOpenChange={(open) =>
        !open &&
        (() => {
          csEventsHandler({ triggerSource: 'button_x_to_close' });
          onClose();
        })()
      }
      size={{ base: 'fullScreen', md: 'md' }}
    >
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content>
            <Modal.Body>
              <div data-testid="SFTotalMonthlyPriceWithFeeAcknowledgementModal">
                <Box css={{ marginBottom: '800' }}>
                  <Image src={ClipboardImage} alt="" />
                </Box>

                <Heading level={2} textStyle="heading-sm" css={{ marginBottom: '400' }}>
                  Confirm the fee information is complete and accurate
                </Heading>

                <div>
                  <List.Root appearance="bullet">
                    <List.Item>
                      Your area has fee transparency regulations that require you to disclose a &ldquo;total
                      price&rdquo; to your renters.{' '}
                      <Anchor
                        target="_blank"
                        href="https://www.zillow.com/rentals-network/pricing-transparency/"
                        onClick={() => {
                          emitLearnMoreEvent({
                            user,
                            currentProperty: webAnalyticsData,
                            triggerObject: 'zrm_costs_and_fees_tmp_modal',
                            triggerLocation: !modularListingProgressStep?.includes('review')
                              ? 'posting_path'
                              : 'posting_path_review',
                          });
                        }}
                        rel="noopener noreferrer"
                        data-eventid={EVENT_IDS.LEARN_MORE}
                      >
                        Learn more
                      </Anchor>
                    </List.Item>

                    <List.Item>
                      To help renters understand the total cost of living in this home, any required monthly fees will
                      be combined with base rent and shown as the listing price.
                    </List.Item>

                    <List.Item>
                      A &ldquo;total monthly price&rdquo; label will also be added to your listing to confirm fee
                      transparency.
                    </List.Item>
                  </List.Root>

                  <Box css={{ marginTop: '400' }}>
                    <Paragraph>
                      By selecting &ldquo;Confirm,&rdquo; you confirm you&apos;ve accurately entered required monthly
                      fees and you want to enable &ldquo; total monthly price&rdquo; display.
                    </Paragraph>
                  </Box>
                </div>
              </div>
            </Modal.Body>
            <Divider />
            <Modal.Footer>
              <ModalFooter
                cancelText="Go back"
                onCancel={() => {
                  csEventsHandler({ triggerSource: 'button_to_go_back' });

                  onClose();
                }}
                submitText="Confirm"
                isSubmitting={isSubmitLoading}
                onSubmit={onSubmitHandler}
                onCancelDataEventId={EVENT_IDS.TOTAL_PRICE_MODAL_INTERACTION}
                onSubmitDataEventId={EVENT_IDS.TOTAL_PRICE_MODAL_INTERACTION}
              />
            </Modal.Footer>
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
};

export default SFTotalMonthlyPriceWithFeeAcknowledgementModal;
